import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

interface Contact {
  name: string;
  aliases: string[];
  canText: boolean;
}

type Channel = 'text' | 'email';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (message: string) => void;
}

function displayName(contact: Contact): string {
  const alias = contact.aliases[0];
  return alias ? `${contact.name} (${alias})` : contact.name;
}

export default function SendMessageDialog({ open, onOpenChange, onSubmit }: Props) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recipient, setRecipient] = useState<string>('');
  const [channel, setChannel] = useState<Channel>('text');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!open || contacts.length > 0) return;
    fetch('/api/contacts', { method: 'POST', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Contact[]) => {
        const sorted = [...data].sort((a, b) => {
          if (a.canText !== b.canText) return a.canText ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
        setContacts(sorted);
        setLoadError(null);
      })
      .catch((err: Error) => setLoadError(err.message || 'Failed to load contacts'));
  }, [open, contacts.length]);

  const selectedContact = useMemo(
    () => contacts.find((c) => c.name === recipient),
    [contacts, recipient]
  );

  useEffect(() => {
    if (!selectedContact) return;
    if (!selectedContact.canText && channel === 'text') {
      setChannel('email');
    }
  }, [selectedContact, channel]);

  const handleRecipientChange = (name: string) => {
    setRecipient(name);
    const next = contacts.find((c) => c.name === name);
    setChannel(next?.canText ? 'text' : 'email');
  };

  const canSubmit = recipient && message.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const body = message.trim();
    const verb = channel === 'text' ? 'Send a text' : 'Send an email';
    onSubmit(`${verb} to ${recipient}: ${body}`);
    onOpenChange(false);
    setRecipient('');
    setChannel('text');
    setMessage('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Message</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="send-message-recipient">Recipient</Label>
            <Select value={recipient} onValueChange={handleRecipientChange}>
              <SelectTrigger id="send-message-recipient">
                <SelectValue placeholder={loadError ? 'Failed to load contacts' : 'Select a family member'} />
              </SelectTrigger>
              <SelectContent>
                {contacts.map((c) => (
                  <SelectItem key={c.name} value={c.name}>
                    {displayName(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Channel</Label>
            <RadioGroup
              value={channel}
              onValueChange={(v) => setChannel(v as Channel)}
              className="flex gap-6"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  id="send-message-channel-text"
                  value="text"
                  disabled={!selectedContact?.canText}
                />
                <Label htmlFor="send-message-channel-text" className="cursor-pointer">Text</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="send-message-channel-email" value="email" />
                <Label htmlFor="send-message-channel-email" className="cursor-pointer">Email</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="send-message-body">Message</Label>
            <Textarea
              id="send-message-body"
              rows={4}
              placeholder="What would you like to say?"
              className="placeholder:text-muted-foreground/50"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
